import { createSelector } from 'reselect';
import { isEmpty, omit } from 'lodash';
import { PaperlessPreferencesContent } from 'services/Content';
import {
  AK_CLIENT_COMM_SERVICE_TYPE_ID,
  AK_CONFIRMATIONS_SERVICE_TYPE_ID,
  AK_PDD_SERVICE_TYPE_ID,
  AK_STATEMENTS_SERVICE_TYPE_ID,
  AK_TAXFORMS_SERVICE_TYPE_ID,
  BR_CLIENT_COMM_SERVICE_TYPE_ID,
  BR_PROSPECTUS_SERVICE_TYPE_ID,
  BR_PROXY_SERVICE_TYPE_ID,
  BR_TAXFORMS_SERVICE_TYPE_ID,
  CLIENT_COMM_SERVICE_TYPE_ID,
  CONFIRMATIONS_SERVICE_TYPE_ID,
  MD_CLIENT_COMM_SERVICE_TYPE_ID,
  MD_CONFIRMATIONS_SERVICE_TYPE_ID,
  MD_PDD_SERVICE_TYPE_ID,
  MD_STATEMENTS_SERVICE_TYPE_ID,
  MD_TAXFORMS_SERVICE_TYPE_ID,
  NAT_CONFIRMATIONS_SERVICE_TYPE_ID,
  NAT_STATEMENTS_SERVICE_TYPE_ID,
  PROSPECTUS_SERVICE_TYPE_ID,
  PROXY_SERVICE_TYPE_ID,
  STATEMENTS_SERVICE_TYPE_ID,
  TAXFORMS_SERVICE_TYPE_ID,
  NAT_CLIENT_COMM_SERVICE_TYPE_ID,
  NAT_TAXFORMS_SERVICE_TYPE_ID,
  NAT_PDD_SERVICE_TYPE_ID,
  LABELS_FOR_PAPERLESS,
  PAPERLESS_OVERVIEW_ENUM,
  LABELS,
  BR_CUSTODY_STATEMENTS_SERVICE_TYPE_ID
} from 'services/PaperlessPreferences';
import {
  generalGroupIdMap,
  YE_PAPER_STATEMENTS_SERVICE_TYPE_ID
} from 'services/Preferences';
import {
  hasOwnedAlaska529,
  hasOwnedBrokerage,
  hasOwnedManaged,
  hasOwnedActivePlus,
  hasOwnedActiveAdvisory,
  hasOwnedMaryland529,
  hasOwnedMutualFunds,
  hasOwnedNational529,
  getOwnedActiveProductTypes
} from 'state/account/AccountSelectors';
import {
  getComplianceSubscriptions,
  getDocumentDeliveryEnrollments
} from 'state/subscriptions/SubscriptionsSelectors';
import {
  isEligibleForRetailStatementsPaperless,
  isEligibleForRetailConfirmsAndProspectus,
  isEligibleForRetailProxy,
  isEligibleForNAT529Paperless,
  isEligibleForMD529Paperless,
  isEligibleForAK529Paperless,
  isSalesforceRepEnabled,
  hasActiveAdvisoryOnly,
  hasActivePlusOnly,
  isEligibleForTaxFormsAndCommunications,
  isEligibleForProxyShareHolder,
  isEligibleForBrokerageCustodyStatements
} from 'state/view/ViewSelectors';
import {
  ACTIVE_ADVISORY,
  ACTIVE_PLUS,
  productMapByNumber
} from 'services/Products';

export const getPaperlessEligibleRules = createSelector(
  hasOwnedManaged,
  hasOwnedMutualFunds,
  hasOwnedBrokerage,
  hasOwnedMaryland529,
  hasOwnedNational529,
  hasOwnedAlaska529,
  isEligibleForRetailProxy,
  isEligibleForRetailConfirmsAndProspectus,
  isEligibleForRetailStatementsPaperless,
  isEligibleForNAT529Paperless,
  isEligibleForMD529Paperless,
  isEligibleForAK529Paperless,
  isEligibleForTaxFormsAndCommunications,
  isEligibleForProxyShareHolder,
  isEligibleForBrokerageCustodyStatements,
  (
    hasOwnedManaged,
    hasOwnedBrokerage,
    hasOwnedMutualFunds,
    hasOwnedMaryland529,
    hasOwnedNational529,
    hasOwnedAlaska529,
    isEligibleForRetailProxy,
    isEligibleForRetailConfirmsAndProspectus,
    isEligibleForRetailStatementsPaperless,
    isEligibleForNAT529Paperless,
    isEligibleForMD529Paperless,
    isEligibleForAK529Paperless,
    isEligibleForTaxFormsAndCommunications,
    isEligibleForProxyShareHolder,
    isEligibleForBrokerageCustodyStatements
  ) => {
    return {
      hasOwnedManaged,
      hasOwnedBrokerage,
      hasOwnedMutualFunds,
      hasOwnedMaryland529,
      hasOwnedNational529,
      hasOwnedAlaska529,
      isEligibleForRetailProxy,
      isEligibleForRetailConfirmsAndProspectus,
      isEligibleForRetailStatementsPaperless,
      isEligibleForNAT529Paperless,
      isEligibleForMD529Paperless,
      isEligibleForAK529Paperless,
      isEligibleForTaxFormsAndCommunications,
      isEligibleForProxyShareHolder,
      isEligibleForBrokerageCustodyStatements
    };
  }
);

export const getPaperlessPreferences = createSelector(
  isSalesforceRepEnabled,
  hasOwnedMutualFunds,
  hasOwnedBrokerage,
  hasOwnedActivePlus,
  hasOwnedActiveAdvisory,
  hasOwnedNational529,
  hasOwnedMaryland529,
  hasOwnedAlaska529,
  getPaperlessEligibleRules,
  (
    isSalesforceRepEnabled,
    hasOwnedMutualFunds,
    hasOwnedBrokerage,
    hasOwnedActivePlus,
    hasOwnedActiveAdvisory,
    hasOwnedNational529,
    hasOwnedMaryland529,
    hasOwnedAlaska529,
    paperlessEligibleRules
  ) => {
    let preferences = {};

    const mutualFundPreferences = [
      {
        title: 'Statements',
        note: 'Note: If Mutual Fund Statements are delivered electronically, Brokerage Statements must also be delivered electronically.',
        hasServiceType: true,
        hasServiceFeeWaiver: true,
        isServiceFeeWaived: true,
        isEmailDelivery: true,
        isReadOnly: false,
        modalContent: PaperlessPreferencesContent.mutualFundStatementsCopy(
          isSalesforceRepEnabled
        ),
        serviceTypeId: STATEMENTS_SERVICE_TYPE_ID,
        isEligible: function (views) {
          // MF or Brokerage or Trac - ask about non-century trac
          // owned
          // logged in user is primary party
          return views.isEligibleForRetailStatementsPaperless;
        }
      },
      {
        title: 'Confirmations',
        note: 'Note: If Mutual Fund Confirmations are delivered electronically, Brokerage Confirmations must also be delivered electronically.',
        hasServiceType: true,
        hasServiceFeeWaiver: true,
        isServiceFeeWaived: true,
        isEmailDelivery: true,
        isReadOnly: false,
        modalContent: PaperlessPreferencesContent.mutualFundConfirmationsCopy(
          isSalesforceRepEnabled
        ),
        serviceTypeId: CONFIRMATIONS_SERVICE_TYPE_ID,
        isEligible: function (views) {
          // MF or Brokerage
          return views.isEligibleForRetailConfirmsAndProspectus;
        }
      },
      {
        title: 'Prospectuses & Shareholder Reports',
        note: 'Note: If Mutual Fund Confirmations are delivered electronically, Prospectuses and Shareholder Reports must also be delivered electronically.',
        hasServiceType: true,
        hasServiceFeeWaiver: true,
        isServiceFeeWaived: true,
        isEmailDelivery: true,
        isReadOnly: false,
        modalContent: PaperlessPreferencesContent.mutualFundProspectusesCopy(
          isSalesforceRepEnabled
        ),
        serviceTypeId: PROSPECTUS_SERVICE_TYPE_ID,
        isEligible: function (views) {
          // MF or Brokerage
          return views.isEligibleForRetailConfirmsAndProspectus;
        },
        hasBrokerageCopy: true,
        showBanner: true
      },
      {
        title: 'Proxy Materials',
        hasServiceType: true,
        hasServiceFeeWaiver: false,
        isEmailDelivery: true,
        isReadOnly: false,
        modalContent: PaperlessPreferencesContent.mutualFundProxyCopy(
          isSalesforceRepEnabled
        ),
        serviceTypeId: PROXY_SERVICE_TYPE_ID,
        isEligible: function (views) {
          // MF
          return views.isEligibleForRetailProxy;
        },
        hasBrokerageCopy: true
      },
      {
        title: 'Tax Forms',
        hasServiceType: true,
        hasServiceFeeWaiver: false,
        isEmailDelivery: true,
        isReadOnly: false,
        modalContent: PaperlessPreferencesContent.mutualFundTaxFormsCopy(
          isSalesforceRepEnabled
        ),
        serviceTypeId: TAXFORMS_SERVICE_TYPE_ID,
        isEligible: function (views) {
          // MF
          return views.isEligibleForTaxFormsAndCommunications;
        },
        hasBrokerageCopy: true
      },
      {
        title: 'Client Communications',
        hasServiceType: true,
        hasServiceFeeWaiver: false,
        isEmailDelivery: true,
        isReadOnly: false,
        modalContent: PaperlessPreferencesContent.mutualFundCommunicationsCopy(
          isSalesforceRepEnabled
        ),
        serviceTypeId: CLIENT_COMM_SERVICE_TYPE_ID,
        isEligible: function (views) {
          // MF
          return views.isEligibleForTaxFormsAndCommunications;
        },
        hasBrokerageCopy: true
      }
    ];
    const brokeragePreferences = [
      {
        title: 'Custody Statements',
        note: '',
        hasServiceType: true,
        hasServiceFeeWaiver: true,
        isServiceFeeWaived: true,
        isEmailDelivery: true,
        isReadOnly: false,
        modalContent: PaperlessPreferencesContent.brokerageCustodyStatementsCopy(
            isSalesforceRepEnabled
        ),
        serviceTypeId: BR_CUSTODY_STATEMENTS_SERVICE_TYPE_ID,
        isEligible: function (views) {
          return views.isEligibleForBrokerageCustodyStatements;
        }
      },
      {
        title: 'Statements',
        note: 'Note: If Brokerage Statements are delivered electronically, Mutual Fund Statements must also be delivered electronically.',
        hasServiceType: true,
        hasServiceFeeWaiver: true,
        isServiceFeeWaived: true,
        isEmailDelivery: true,
        isReadOnly: false,
        modalContent: PaperlessPreferencesContent.brokerageStatementsCopy(
          isSalesforceRepEnabled
        ),
        serviceTypeId: STATEMENTS_SERVICE_TYPE_ID,
        isEligible: function (views) {
          // MF or Brokerage or Trac - ask about non-century trac
          // owned
          // logged in user is primary party
          return views.isEligibleForRetailStatementsPaperless;
        }
      },
      {
        title: 'Confirmations',
        note: 'Note: If Brokerage Confirmations are delivered electronically, Mutual Fund Confirmations must also be delivered electronically.',
        hasServiceType: true,
        hasServiceFeeWaiver: true,
        isServiceFeeWaived: true,
        isEmailDelivery: true,
        isReadOnly: false,
        modalContent: PaperlessPreferencesContent.brokerageConfirmationsCopy(
          isSalesforceRepEnabled
        ),
        serviceTypeId: CONFIRMATIONS_SERVICE_TYPE_ID,
        isEligible: function (views) {
          // MF or Brokerage
          return views.isEligibleForRetailConfirmsAndProspectus;
        }
      },
      {
        title: 'Prospectuses',
        hasServiceType: true,
        hasServiceFeeWaiver: true,
        isServiceFeeWaived: true,
        isEmailDelivery: true,
        isReadOnly: false,
        modalContent: PaperlessPreferencesContent.brokerageProspectusesCopy(
          isSalesforceRepEnabled
        ),
        serviceTypeId: BR_PROSPECTUS_SERVICE_TYPE_ID,
        isEligible: function (views) {
          // MF or Brokerage
          return views.isEligibleForRetailConfirmsAndProspectus;
        },
        hasBrokerageCopy: true,
        showBanner: true
      },
      {
        title: 'Proxy/Shareholder Communications',
        hasServiceType: true,
        hasServiceFeeWaiver: false,
        isEmailDelivery: true,
        isReadOnly: false,
        modalContent: PaperlessPreferencesContent.brokerageProxyCopy(
          isSalesforceRepEnabled
        ),
        serviceTypeId: BR_PROXY_SERVICE_TYPE_ID,
        isEligible: function (views) {
          return views.isEligibleForProxyShareHolder;
        },
        hasBrokerageCopy: true
      },
      {
        title: 'Tax Forms',
        hasServiceType: true,
        hasServiceFeeWaiver: false,
        isEmailDelivery: true,
        isReadOnly: false,
        modalContent: PaperlessPreferencesContent.brokerageTaxFormsCopy(
          isSalesforceRepEnabled
        ),
        serviceTypeId: BR_TAXFORMS_SERVICE_TYPE_ID,
        isEligible: function (views) {
          return views.isEligibleForTaxFormsAndCommunications;
        },
        hasBrokerageCopy: true
      },
      {
        title: 'Client Communications',
        hasServiceType: true,
        hasServiceFeeWaiver: false,
        isEmailDelivery: true,
        isReadOnly: false,
        modalContent: PaperlessPreferencesContent.brokerageCommunicationsCopy(
          isSalesforceRepEnabled
        ),
        serviceTypeId: BR_CLIENT_COMM_SERVICE_TYPE_ID,
        isEligible: function (views) {
          return views.isEligibleForTaxFormsAndCommunications;
        },
        hasBrokerageCopy: true
      }
    ];
    const trpCollegeSavings = [
      {
        title: 'Statements',
        hasServiceType: true,
        hasServiceFeeWaiver: false,
        isEmailDelivery: true,
        isReadOnly: false,
        modalContent: PaperlessPreferencesContent.natStatementsCopy(
          isSalesforceRepEnabled
        ),
        serviceTypeId: NAT_STATEMENTS_SERVICE_TYPE_ID,
        isEligible: function () {
          return isEligibleForNAT529Paperless;
        }
      },
      {
        title: 'Confirmations',
        hasServiceType: true,
        hasServiceFeeWaiver: false,
        isEmailDelivery: true,
        isReadOnly: false,
        modalContent: PaperlessPreferencesContent.natConfirmationsCopy(
          isSalesforceRepEnabled
        ),
        serviceTypeId: NAT_CONFIRMATIONS_SERVICE_TYPE_ID,
        isEligible: function () {
          return isEligibleForNAT529Paperless;
        }
      },
      {
        title: 'Plan Disclosure Documents',
        hasServiceType: true,
        hasServiceFeeWaiver: false,
        isEmailDelivery: true,
        isReadOnly: false,
        modalContent: PaperlessPreferencesContent.natPlanDisclosuresCopy(
          isSalesforceRepEnabled
        ),
        serviceTypeId: NAT_PDD_SERVICE_TYPE_ID,
        isEligible: function () {
          return isEligibleForNAT529Paperless;
        }
      },
      {
        title: 'Tax Forms',
        hasServiceType: true,
        hasServiceFeeWaiver: false,
        isEmailDelivery: true,
        isReadOnly: false,
        modalContent: PaperlessPreferencesContent.natTaxFormsCopy(
          isSalesforceRepEnabled
        ),
        serviceTypeId: NAT_TAXFORMS_SERVICE_TYPE_ID,
        isEligible: function () {
          return isEligibleForNAT529Paperless;
        },
        hasBrokerageCopy: true
      },
      {
        title: 'Client Communications',
        hasServiceType: true,
        hasServiceFeeWaiver: false,
        isEmailDelivery: true,
        isReadOnly: false,
        modalContent: PaperlessPreferencesContent.natCommunicationsCopy(
          isSalesforceRepEnabled
        ),
        serviceTypeId: NAT_CLIENT_COMM_SERVICE_TYPE_ID,
        isEligible: function () {
          return isEligibleForNAT529Paperless;
        },
        hasBrokerageCopy: true
      }
    ];
    const mdCollegeSavings = [
      {
        title: 'Statements',
        hasServiceType: true,
        hasServiceFeeWaiver: false,
        isEmailDelivery: true,
        isReadOnly: false,
        modalContent: PaperlessPreferencesContent.mdStatementsCopy(
          isSalesforceRepEnabled
        ),
        serviceTypeId: MD_STATEMENTS_SERVICE_TYPE_ID,
        isEligible: function () {
          return isEligibleForMD529Paperless;
        }
      },
      {
        title: 'Confirmations',
        hasServiceType: true,
        hasServiceFeeWaiver: false,
        isEmailDelivery: true,
        isReadOnly: false,
        modalContent: PaperlessPreferencesContent.mdConfirmationsCopy(
          isSalesforceRepEnabled
        ),
        serviceTypeId: MD_CONFIRMATIONS_SERVICE_TYPE_ID,
        isEligible: function () {
          return isEligibleForMD529Paperless;
        }
      },
      {
        title: 'Plan Disclosure Statements',
        hasServiceType: true,
        hasServiceFeeWaiver: false,
        isEmailDelivery: true,
        isReadOnly: false,
        modalContent: PaperlessPreferencesContent.mdPlanDisclosuresCopy(
          isSalesforceRepEnabled
        ),
        serviceTypeId: MD_PDD_SERVICE_TYPE_ID,
        isEligible: function () {
          return isEligibleForMD529Paperless;
        }
      },
      {
        title: 'Tax Forms',
        hasServiceType: true,
        hasServiceFeeWaiver: false,
        isEmailDelivery: true,
        isReadOnly: false,
        modalContent: PaperlessPreferencesContent.mdTaxFormsCopy(
          isSalesforceRepEnabled
        ),
        serviceTypeId: MD_TAXFORMS_SERVICE_TYPE_ID,
        isEligible: function () {
          return isEligibleForMD529Paperless;
        },
        hasBrokerageCopy: true
      },
      {
        title: 'Client Communications',
        hasServiceType: true,
        hasServiceFeeWaiver: false,
        isEmailDelivery: true,
        isReadOnly: false,
        modalContent: PaperlessPreferencesContent.mdCommunicationsCopy(
          isSalesforceRepEnabled
        ),
        serviceTypeId: MD_CLIENT_COMM_SERVICE_TYPE_ID,
        isEligible: function () {
          return isEligibleForMD529Paperless;
        },
        hasBrokerageCopy: true
      }
    ];
    const akCollegeSavings = [
      {
        title: 'Statements',
        hasServiceType: true,
        hasServiceFeeWaiver: false,
        isEmailDelivery: true,
        isReadOnly: false,
        modalContent: PaperlessPreferencesContent.akStatemensCopy(
          isSalesforceRepEnabled
        ),
        serviceTypeId: AK_STATEMENTS_SERVICE_TYPE_ID,
        isEligible: function () {
          return isEligibleForAK529Paperless;
        }
      },
      {
        title: 'Confirmations',
        hasServiceType: true,
        hasServiceFeeWaiver: false,
        isEmailDelivery: true,
        isReadOnly: false,
        modalContent: PaperlessPreferencesContent.akConfirmationsCopy(
          isSalesforceRepEnabled
        ),
        serviceTypeId: AK_CONFIRMATIONS_SERVICE_TYPE_ID,
        isEligible: function () {
          return isEligibleForAK529Paperless;
        }
      },
      {
        title: 'Plan Disclosure Documents',
        hasServiceType: true,
        hasServiceFeeWaiver: false,
        isEmailDelivery: true,
        isReadOnly: false,
        modalContent: PaperlessPreferencesContent.akPlanDisclosuresCopy(
          isSalesforceRepEnabled
        ),
        serviceTypeId: AK_PDD_SERVICE_TYPE_ID,
        isEligible: function () {
          return isEligibleForAK529Paperless;
        }
      },
      {
        title: 'Tax Forms',
        hasServiceType: true,
        hasServiceFeeWaiver: false,
        isEmailDelivery: true,
        isReadOnly: false,
        modalContent: PaperlessPreferencesContent.akTaxFormsCopy(
          isSalesforceRepEnabled
        ),
        serviceTypeId: AK_TAXFORMS_SERVICE_TYPE_ID,
        isEligible: function () {
          return isEligibleForAK529Paperless;
        },
        hasBrokerageCopy: true
      },
      {
        title: 'Client Communications',
        hasServiceType: true,
        hasServiceFeeWaiver: false,
        isEmailDelivery: true,
        isReadOnly: false,
        modalContent: PaperlessPreferencesContent.akCommunicationsCopy(
          isSalesforceRepEnabled
        ),
        serviceTypeId: AK_CLIENT_COMM_SERVICE_TYPE_ID,
        isEligible: function () {
          return isEligibleForAK529Paperless;
        },
        hasBrokerageCopy: true
      }
    ];

    const activePlusPreferences = [
      {
        disableMailSelection: true,
        title: 'All Client Communications, Documents, and Disclosures',
        hasServiceType: true,
        hasServiceFeeWaiver: false,
        modalContent: PaperlessPreferencesContent.activePlusCopy(
          isSalesforceRepEnabled
        ),
        isEmailDelivery: true,
        isReadOnly: true
      }
    ];

    const activeAdvisoryPreferences = [
      {
        disableMailSelection: true,
        title: 'All Client Communications, Documents, and Disclosures',
        hasServiceType: true,
        hasServiceFeeWaiver: false,
        modalContent: PaperlessPreferencesContent.activeAdvisoryCopy(
          isSalesforceRepEnabled
        ),
        isEmailDelivery: true,
        isReadOnly: true
      }
    ];

    if (hasOwnedBrokerage) {
      preferences.brokerage = brokeragePreferences.filter(({ isEligible }) =>
        isEligible(paperlessEligibleRules)
      );
    }

    if (hasOwnedMutualFunds) {
      preferences.mutualfund = mutualFundPreferences.filter(({ isEligible }) =>
        isEligible(paperlessEligibleRules)
      );
    }

    if (hasOwnedNational529) {
      preferences.nationalsavings = trpCollegeSavings.filter(({ isEligible }) =>
        isEligible(paperlessEligibleRules)
      );
    }

    if (hasOwnedMaryland529) {
      preferences.marylandsavings = mdCollegeSavings.filter(({ isEligible }) =>
        isEligible(paperlessEligibleRules)
      );
    }

    if (hasOwnedAlaska529) {
      preferences.alaskasavings = akCollegeSavings.filter(({ isEligible }) =>
        isEligible(paperlessEligibleRules)
      );
    }

    if (hasOwnedActivePlus) {
      preferences.activeplus = activePlusPreferences;
    }
    if (hasOwnedActiveAdvisory) {
      preferences.activeadvisory = activeAdvisoryPreferences;
    }

    return preferences;
  }
);

export const getEligibleServiceTypeIds = createSelector(
  getPaperlessEligibleRules,
  getPaperlessPreferences,
  (paperlessEligibleRules, paperlessPreferencesSettings) => {
    return (
      paperlessPreferencesSettings &&
      paperlessPreferencesSettings.mutualfund &&
      paperlessPreferencesSettings.mutualfund
        .filter(({ isEligible }) => isEligible(paperlessEligibleRules))
        .map(({ serviceTypeId }) => serviceTypeId)
    );
  }
);

export const isMFAccountServiceFeeWaived = createSelector(
  getDocumentDeliveryEnrollments,
  (documentDelivery) => {
    return Object.values(generalGroupIdMap).every(
      (serviceTypeId) => documentDelivery[serviceTypeId]
    );
  }
);

export const isFullyPaperlessPreferences = createSelector(
  getDocumentDeliveryEnrollments,
  getPaperlessPreferences,
  hasActivePlusOnly,
  hasActiveAdvisoryOnly,
  (
    documentDelivery,
    paperlessPreferencesSettings,
    hasActivePlusOnly,
    hasActiveAdvisoryOnly
  ) => {
    let isFullyPaperless = false;

    // we dont have service type id fot activeplus & activeadvisory documents, removing those from servicetype id verification.
    const OMITTED_PRODUCTS = ['activeadvisory', 'activeplus'];
    let paperlessSettings = omit(
      paperlessPreferencesSettings,
      OMITTED_PRODUCTS
    );

    const keys = Object.keys(paperlessSettings);

    /* If user has RAS only or APP only user, document selected to Email by default.
     In this case it has to be fully paperless (RAS only || APP only user) */

    if (hasActivePlusOnly || hasActiveAdvisoryOnly) {
      return true;
    }

    for (const key of keys) {
      if (
        paperlessSettings[key].every(
          (document) => documentDelivery[document.serviceTypeId]
        )
      ) {
        isFullyPaperless = true;
      } else {
        return false;
      }
    }
    return isFullyPaperless;
  }
);

export const getPaperlessPreferencesInitialEnrollments = createSelector(
  getPaperlessPreferences,
  (paperlessPreferencesSettings) => {
    const OMITTED_PRODUCTS = ['activeadvisory', 'activeplus'];
    let preferencesSettings = omit(
      paperlessPreferencesSettings,
      OMITTED_PRODUCTS
    );
    const combinedDocuments = Object.values(preferencesSettings).flat();
    let defaultEnrollments = {};
    combinedDocuments.forEach(
      (el) => (defaultEnrollments[el.serviceTypeId] = false)
    );
    return defaultEnrollments;
  }
);

export const getUpdatedDocumentDeliveryEnrollments = createSelector(
  getComplianceSubscriptions,
  getPaperlessPreferences,
  getPaperlessPreferencesInitialEnrollments,
  (complianceSubscriptions, paperlessPreferencesSettings, initialEnrollments) => {
    //Filter based on the paperlessPreferencesSettings that use the isEligible logic based on account ownership
    const combinedDocuments = Object.values(paperlessPreferencesSettings).flat();
    let subscriptions = complianceSubscriptions.filter(subscription => {
      //YE is not part of the overall paperless settings object, so we cant filter that out
      if(subscription.subscriptionId === YE_PAPER_STATEMENTS_SERVICE_TYPE_ID) {
        return true;
      }
      //If the subscriptionId is found after the flat, that means it was not filtered out by the isEligible logic
      return combinedDocuments.find(x => subscription.subscriptionId === x.serviceTypeId);
    })
      .reduce(
      (acc, { subscriptionId, subscribed }) => ({
        ...acc,
        [subscriptionId]: subscribed
      }),
      {}
    );

    return { ...initialEnrollments, ...subscriptions };
  }
);

export const getFilteredPaperlessEnrollments = createSelector(
  getUpdatedDocumentDeliveryEnrollments,
  getOwnedActiveProductTypes,
  (enrollments, ownedProducts) => {
    let filteredEnrollments = [];
    if (!isEmpty(enrollments)) {
      const tempEnrollments = Object.keys(enrollments).map((enrollment) => {
        return {
          subscriptionId: parseInt(enrollment),
          subscribed: enrollments[enrollment]
        };
      });
      ownedProducts
        .filter(
          (product) => product !== ACTIVE_PLUS && product !== ACTIVE_ADVISORY
        )
        .forEach((product) => {
          const filteredEnrollmentsByProduct = tempEnrollments.filter(
            (enrollment) => {
              const enumeration =
                PAPERLESS_OVERVIEW_ENUM[productMapByNumber[product]];
              return enumeration.includes(enrollment.subscriptionId);
            }
          );
          if (filteredEnrollmentsByProduct.length) {
            filteredEnrollments.push(...filteredEnrollmentsByProduct);
          }
        });
    }

    return filteredEnrollments;
  }
);

export const getPaperlessPreferencesOverview = createSelector(
  getFilteredPaperlessEnrollments,
  hasOwnedActiveAdvisory,
  hasOwnedActivePlus,
  (enrollments, hasOwnedActiveAdvisory, hasOwnedActivePlus) => {
    let status = [];

    if (enrollments && enrollments.length) {
      LABELS.forEach((label) => {
        const paperless = enrollments.filter((enrollment) =>
          LABELS_FOR_PAPERLESS[label].includes(enrollment.subscriptionId)
        );
        if (paperless.length) {
          const isAllPaperless = paperless.every((paper) => paper.subscribed);
          status.push({ label, isAllPaperless });
        }
      });
    }
    //repeat for Retirement Advisory Service:
    if (hasOwnedActiveAdvisory) {
      //if user is valid for Retirement Advisory Service, their prefs are paperless by default
      let activeAdvisory = { label: 'Advisory', isAllPaperless: true }; //create object for Retirement Advisory Service
      status.push(activeAdvisory); //push Retirement Advisory Service preferences into the array
    }
    //repeat for Active Plus Portfolios:
    if (hasOwnedActivePlus) {
      //if user is valid for Active Plus Portfolios, their prefs are paperless by default
      let activePlus = { label: 'ActivePlus', isAllPaperless: true }; //create object for Active Plus Portfolios
      status.push(activePlus); //push Active Plus Portfolios preferences into the array
    }
    return status.sort((a, b) => b.isAllPaperless - a.isAllPaperless);
  }
);
